import { Text } from "@/components/Typography/Text";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/cn";
import { defaultNS } from "@/lib/i18n";
import { useTranslation } from "react-i18next";
import { TABLE_CONFIG } from "./constants";
import type { CategoryData, ColumnIndexer } from "./types";

interface TableHeaderProps {
  categories: CategoryData[];
  hoveredColIndex: number | null;
  setHoveredColIndex: (index: number | null) => void;
  columnIndexer: ColumnIndexer;
  isScrolling?: boolean;
}

export const TableHeaderComponent = ({
  categories,
  hoveredColIndex,
  setHoveredColIndex,
  columnIndexer,
  isScrolling = false,
}: TableHeaderProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.operationPackageInstance.tableHeader",
  });

  // 固定列ラベルの取得
  const fixedColumns = TABLE_CONFIG.FIXED_COLUMNS;

  return (
    <TableHeader className="sticky top-0 z-20 bg-neutral-gray-light-3 border-neutral-on-fill border-y">
      {/* ヘッダー1行目 - グループカテゴリー */}
      <TableRow>
        <TableHead
          colSpan={columnIndexer.fixedColumnCount}
          className={cn(
            "sticky left-0 z-30 bg-neutral-gray-light-3 border-b-2 border-neutral-gray-light-3",
            isScrolling ? "shadow-md" : "",
          )}
        />
        {categories.map((largeCategory, _index) => (
          <TableHead
            key={largeCategory.code}
            colSpan={largeCategory.mediumCategories.length}
            className={cn(
              "text-start px-4 border-b-2 border-neutral-gray-light-3",
            )}
          >
            <Text variant="label" className="text-neutral-muted-on-fill">
              {largeCategory.name}
            </Text>
          </TableHead>
        ))}
      </TableRow>

      {/* ヘッダー2行目 - 詳細カテゴリー */}
      <TableRow>
        {/* 固定カラムのヘッダー */}
        {fixedColumns.map((column, index) => (
          <TableHead
            key={column.id}
            className={cn(
              "sticky z-40 bg-neutral-gray-light-3",
              index === 0 ? "left-0" : "left-32",
            )}
            onMouseEnter={() => setHoveredColIndex(column.index)}
            onMouseLeave={() => setHoveredColIndex(null)}
          >
            <Text variant="label" className="text-neutral-muted-on-fill">
              {t(column.id)}
            </Text>
          </TableHead>
        ))}

        {/* カテゴリーカラムのヘッダー */}
        {categories.flatMap((largeCategory) =>
          largeCategory.mediumCategories.map((mediumCategory) => {
            const colIndex = columnIndexer.getCategoryColumnIndex(
              largeCategory,
              mediumCategory,
            );

            return (
              <TableHead
                key={`${largeCategory.code}_${mediumCategory.code}`}
                className={cn(
                  "p-2 text-center relative",
                  hoveredColIndex === colIndex && "bg-neutral-gray-light-3",
                )}
                onMouseEnter={() => setHoveredColIndex(colIndex)}
                onMouseLeave={() => setHoveredColIndex(null)}
              >
                <Text variant="label" className="text-neutral-muted-on-fill">
                  {mediumCategory.name}
                </Text>
              </TableHead>
            );
          }),
        )}
      </TableRow>
    </TableHeader>
  );
};
